import React, { useState, useMemo } from 'react';
import {
  X,
  Search,
  Calendar,
  Clock,
  Check,
  TrendingUp,
  Clock3,
  CalendarDays,
  ChevronRight,
  Sparkles,
  CheckCircle2
} from 'lucide-react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import SetReminderModal from './SetReminderModal';

const SetCollectionDateModal = ({
  isOpen,
  onClose,
  customers = [],
  totalOutstanding = 0,
  onSaveReminderDate,
  onBulkSetDate,
}) => {
  const { t } = useLang();

  const [selectedIds, setSelectedIds] = useState([]);
  const [amountFilter, setAmountFilter] = useState('all'); // all, 1k-10k, 10k-30k, 30k-50k, 50k+
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [sortBy, setSortBy] = useState('highest_amount'); // highest_amount, oldest_dues, by_due_date
  const [searchQuery, setSearchQuery] = useState('');

  const [singleCustomerModal, setSingleCustomerModal] = useState(null);
  const [isBulkDateModalOpen, setIsBulkDateModalOpen] = useState(false);


  // Filter & Sort Logic matching Flutter Mobile App
  const filteredList = useMemo(() => {
    let list = [...customers];

    // 1. Amount preset filter
    if (amountFilter === '1k-10k') {
      list = list.filter((c) => c.pending_balance >= 1000 && c.pending_balance <= 10000);
    } else if (amountFilter === '10k-30k') {
      list = list.filter((c) => c.pending_balance > 10000 && c.pending_balance <= 30000);
    } else if (amountFilter === '30k-50k') {
      list = list.filter((c) => c.pending_balance > 30000 && c.pending_balance <= 50000);
    } else if (amountFilter === '50k+') {
      list = list.filter((c) => c.pending_balance > 50000);
    }

    // 2. Custom min / max
    if (minAmount && !isNaN(minAmount)) {
      list = list.filter((c) => c.pending_balance >= parseFloat(minAmount));
    }
    if (maxAmount && !isNaN(maxAmount)) {
      list = list.filter((c) => c.pending_balance <= parseFloat(maxAmount));
    }

    // 3. Search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (c) =>
          c.customer_name?.toLowerCase().includes(q) ||
          c.mobile?.includes(q)
      );
    }

    // 4. Sort
    list.sort((a, b) => {
      if (sortBy === 'highest_amount') {
        return parseFloat(b.pending_balance || 0) - parseFloat(a.pending_balance || 0);
      } else if (sortBy === 'oldest_dues') {
        const dateA = a.last_transaction_date ? new Date(a.last_transaction_date) : new Date(0);
        const dateB = b.last_transaction_date ? new Date(b.last_transaction_date) : new Date(0);
        return dateA - dateB;
      } else if (sortBy === 'by_due_date') {
        if (!a.reminder_date) return 1;
        if (!b.reminder_date) return -1;
        return new Date(a.reminder_date) - new Date(b.reminder_date);
      }
      return 0;
    });

    return list;
  }, [customers, amountFilter, minAmount, maxAmount, sortBy, searchQuery]);

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === filteredList.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredList.map((c) => c.customer_id));
    }
  };

  const formatCurrency = (val) => {
    return `₹${parseFloat(val || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr || dateStr === '0000-00-00' || dateStr === 'null') return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      const d = new Date(year, month, day);
      return d.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: '2-digit',
      });
    }
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: '2-digit',
    });
  };

  const formatSentDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getInitials = (name) => {
    if (!name) return 'C';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const selectedTotal = useMemo(() => {
    return customers
      .filter((c) => selectedIds.includes(c.customer_id))
      .reduce((sum, c) => sum + parseFloat(c.pending_balance || 0), 0);
  }, [customers, selectedIds]);

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* TOP BLUE HEADER (Matching Screenshot 5) */}
        <div style={styles.topHeader}>
          <div style={styles.navRow}>
            <button style={styles.backBtn} onClick={onClose}>
              ‹
            </button>
            <span style={styles.headerTitle}>
              {t(LangKeys.reminders_set_collection_date) || 'Set Collection Date'}
            </span>
            <button style={styles.selectAllBtn} onClick={handleSelectAll}>
              <span
                style={{
                  ...styles.radioCircle,
                  backgroundColor:
                    selectedIds.length === filteredList.length && filteredList.length > 0
                      ? '#FFFFFF'
                      : 'transparent',
                }}
              />
              <span>{t(LangKeys.reminders_select_all) || 'Select All'}</span>
            </button>
          </div>

          <div style={styles.headerHeroRow}>
            <div>
              <div style={styles.heroAmount}>{formatCurrency(totalOutstanding)}</div>
              <div style={styles.heroSub}>
                {customers.length} {t(LangKeys.reminders_collect_faster_sub) || 'Collect faster from customers'}
              </div>
            </div>
            <div style={styles.rupeeIconCircle}>
              <span>₹</span>
            </div>
          </div>
        </div>

        {/* BODY CONTAINER */}
        <div style={styles.body}>
          {/* PENDING AMOUNT FILTER CARD */}
          <div style={styles.filterCard}>
            <div style={styles.filterCardTitle}>
              <span>📶</span>
              <span>{t(LangKeys.reminders_pending_amount_filter) || 'Pending Amount Filter (₹)'}</span>
            </div>

            {/* Chips */}
            <div style={styles.chipsRow}>
              {[
                { id: 'all', label: t(LangKeys.reminders_all) || 'All' },
                { id: '1k-10k', label: '₹1k - ₹10k' },
                { id: '10k-30k', label: '₹10k - ₹30k' },
                { id: '30k-50k', label: '₹30k - ₹50k' },
                { id: '50k+', label: '₹50k+' },
              ].map((chip) => {
                const isActive = amountFilter === chip.id;
                return (
                  <button
                    key={chip.id}
                    style={{
                      ...styles.amountChip,
                      ...(isActive ? styles.amountChipActive : {}),
                    }}
                    onClick={() => {
                      setAmountFilter(chip.id);
                      setMinAmount('');
                      setMaxAmount('');
                    }}
                  >
                    {chip.label}
                  </button>
                );
              })}
            </div>

            {/* Min / Max Inputs */}
            <div style={styles.rangeRow}>
              <div style={styles.rangeCol}>
                <input
                  type="number"
                  placeholder={`₹ ${t(LangKeys.reminders_min) || 'Min'} ₹`}
                  value={minAmount}
                  onChange={(e) => {
                    setMinAmount(e.target.value);
                    setAmountFilter('custom');
                  }}
                  style={styles.rangeInput}
                />
              </div>
              <span style={styles.rangeToText}>{t(LangKeys.reminders_to) || 'to'}</span>
              <div style={styles.rangeCol}>
                <input
                  type="number"
                  placeholder={`₹ ${t(LangKeys.reminders_max) || 'Max'} ₹`}
                  value={maxAmount}
                  onChange={(e) => {
                    setMaxAmount(e.target.value);
                    setAmountFilter('custom');
                  }}
                  style={styles.rangeInput}
                />
              </div>
            </div>
          </div>

          {/* SORT BUTTONS ROW */}
          <div style={styles.sortRow}>
            <button
              style={{
                ...styles.sortBtn,
                ...(sortBy === 'highest_amount' ? styles.sortBtnActive : {}),
              }}
              onClick={() => setSortBy('highest_amount')}
            >
              <TrendingUp size={14} />
              <span>{t(LangKeys.reminders_highest_amount) || 'Highest Amount'}</span>
            </button>

            <button
              style={{
                ...styles.sortBtn,
                ...(sortBy === 'oldest_dues' ? styles.sortBtnActive : {}),
              }}
              onClick={() => setSortBy('oldest_dues')}
            >
              <Clock3 size={14} />
              <span>{t(LangKeys.reminders_oldest_dues) || 'Oldest Dues'}</span>
            </button>

            <button
              style={{
                ...styles.sortBtn,
                ...(sortBy === 'by_due_date' ? styles.sortBtnActive : {}),
              }}
              onClick={() => setSortBy('by_due_date')}
            >
              <CalendarDays size={14} />
              <span>{t(LangKeys.reminders_by_due_date) || 'By Due Date'}</span>
            </button>
          </div>

          {/* SEARCH INPUT */}
          <div style={styles.searchBox}>
            <Search size={16} color="#94A3B8" />
            <input
              type="text"
              placeholder={`${filteredList.length} ${t(LangKeys.reminders_search_placeholder) || 'Search by customer name or mobile...'}`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.searchInput}
            />
            {searchQuery && (
              <button style={styles.clearBtn} onClick={() => setSearchQuery('')}>
                ✕
              </button>
            )}
          </div>

          {/* CUSTOMER LIST */}
          <div style={styles.customerList}>
            {filteredList.map((c) => {
              const isSelected = selectedIds.includes(c.customer_id);
              const initials = getInitials(c.customer_name);

              return (
                <div
                  key={c.customer_id}
                  style={{
                    ...styles.customerRow,
                    ...(isSelected ? styles.customerRowSelected : {}),
                  }}
                  onClick={() => handleToggleSelect(c.customer_id)}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleToggleSelect(c.customer_id)}
                    style={styles.checkbox}
                    onClick={(e) => e.stopPropagation()}
                  />

                  <div style={styles.initialsAvatar}>{initials}</div>

                  <div style={styles.customerInfo}>
                    <div style={styles.rowTop}>
                      <span style={styles.custName}>{c.customer_name}</span>
                      <span style={styles.custAmount}>{formatCurrency(c.pending_balance)}</span>
                    </div>

                    <div style={styles.rowBottom}>
                      <span style={styles.txnDate}>
                        📅 Txn: {c.last_transaction_date ? formatDateDisplay(c.last_transaction_date) : 'N/A'}
                      </span>
                    </div>

                    {c.last_reminder_sent_at && (
                      <div style={styles.sentBadgeRow}>
                        <span style={styles.sentPill}>
                          ✓ {t(LangKeys.reminders_sent) || 'Sent'}: {formatSentDateDisplay(c.last_reminder_sent_at)}
                        </span>
                      </div>
                    )}
                  </div>

                  <button
                    style={styles.setDueBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSingleCustomerModal(c);
                    }}
                  >
                    <span style={{ fontSize: '14px', marginRight: '4px' }}>⊕</span>
                    <span>
                      {c.reminder_date
                        ? formatDateDisplay(c.reminder_date)
                        : (t(LangKeys.reminders_set_due_date) || 'Set Due Date')}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* BOTTOM ACTION BAR WHEN 1+ SELECTED */}
        {selectedIds.length > 0 && (
          <div style={styles.bottomBar}>
            <div>
              <div style={styles.bottomBarCount}>
                {selectedIds.length} {t(LangKeys.reminders_customers_selected) || 'Customers Selected'}
              </div>
              <div style={styles.bottomBarTotal}>
                {formatCurrency(selectedTotal)} {t(LangKeys.reminders_total) || 'Total'}
              </div>
            </div>
            <button
              style={styles.bottomSetDateBtn}
              onClick={() => setIsBulkDateModalOpen(true)}
            >
              <Calendar size={16} />
              <span>{t(LangKeys.reminders_set_due_date_selected) || 'Set Due Date for Selected'}</span>
            </button>
          </div>
        )}
      </div>

      {/* SINGLE CUSTOMER SET REMINDER MODAL */}
      {singleCustomerModal && (
        <SetReminderModal
          isOpen={!!singleCustomerModal}
          onClose={() => setSingleCustomerModal(null)}
          customer={singleCustomerModal}
          onSave={async (payload) => {
            await onSaveReminderDate(payload);
            setSingleCustomerModal(null);
          }}
        />
      )}

      {/* BULK SET DATE MODAL */}
      {isBulkDateModalOpen && (
        <SetReminderModal
          isOpen={isBulkDateModalOpen}
          onClose={() => setIsBulkDateModalOpen(false)}
          isBulk={true}
          bulkIds={selectedIds}
          onSave={async (payload) => {
            await onBulkSetDate(payload);
            setIsBulkDateModalOpen(false);
            setSelectedIds([]);
          }}
        />
      )}
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.7)',
    backdropFilter: 'blur(6px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '16px',
  },
  modal: {
    width: '100%',
    maxWidth: '560px',
    height: '92vh',
    maxHeight: '820px',
    backgroundColor: '#F8FAFC',
    borderRadius: '24px',
    boxShadow: '0 25px 60px rgba(0, 0, 0, 0.35)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
  },
  topHeader: {
    background: 'linear-gradient(135deg, #1D4ED8 0%, #2563EB 50%, #3B82F6 100%)',
    padding: '18px 20px 22px 20px',
    color: '#FFFFFF',
    borderBottomLeftRadius: '24px',
    borderBottomRightRadius: '24px',
    boxShadow: '0 8px 20px rgba(37, 99, 235, 0.25)',
  },
  navRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: '#FFFFFF',
    fontSize: '28px',
    cursor: 'pointer',
    padding: '0 8px',
    lineHeight: '1',
  },
  headerTitle: {
    fontSize: '17px',
    fontWeight: '700',
    color: '#FFFFFF',
  },
  selectAllBtn: {
    background: 'rgba(255, 255, 255, 0.15)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '20px',
    padding: '6px 14px',
    color: '#FFFFFF',
    fontSize: '12.5px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  radioCircle: {
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    border: '1.5px solid #FFFFFF',
  },
  headerHeroRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4px 8px',
  },
  heroAmount: {
    fontSize: '32px',
    fontWeight: '800',
    letterSpacing: '-0.02em',
    color: '#FFFFFF',
  },
  heroSub: {
    fontSize: '13px',
    color: '#DBEAFE',
    marginTop: '2px',
    fontWeight: '500',
  },
  rupeeIconCircle: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#3B82F6',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    fontWeight: '800',
    color: '#FACC15',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  filterCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    padding: '14px',
    border: '1px solid #E2E8F0',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.03)',
  },
  filterCardTitle: {
    fontSize: '12.5px',
    fontWeight: '700',
    color: '#334155',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '10px',
  },
  chipsRow: {
    display: 'flex',
    gap: '6px',
    overflowX: 'auto',
    paddingBottom: '4px',
    marginBottom: '10px',
  },
  amountChip: {
    padding: '6px 12px',
    borderRadius: '16px',
    border: '1px solid #E2E8F0',
    backgroundColor: '#F8FAFC',
    color: '#475569',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  amountChipActive: {
    backgroundColor: '#2563EB',
    color: '#FFFFFF',
    borderColor: '#2563EB',
  },
  rangeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  rangeCol: {
    flex: 1,
  },
  rangeInput: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '10px',
    border: '1px solid #CBD5E1',
    fontSize: '13px',
    color: '#0F172A',
    boxSizing: 'border-box',
    outline: 'none',
    backgroundColor: '#FFFFFF',
  },
  rangeToText: {
    fontSize: '12px',
    color: '#94A3B8',
    fontWeight: '600',
  },
  sortRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  sortBtn: {
    flex: 1,
    padding: '8px 10px',
    borderRadius: '12px',
    border: '1px solid #E2E8F0',
    backgroundColor: '#FFFFFF',
    color: '#475569',
    fontSize: '12px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    cursor: 'pointer',
  },
  sortBtnActive: {
    backgroundColor: '#0F172A',
    color: '#FFFFFF',
    borderColor: '#0F172A',
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 14px',
    borderRadius: '12px',
    backgroundColor: '#FFFFFF',
    border: '1.5px solid #E2E8F0',
  },
  searchInput: {
    flex: 1,
    border: 'none',
    backgroundColor: 'transparent',
    outline: 'none',
    fontSize: '13.5px',
    color: '#0F172A',
  },
  clearBtn: {
    background: 'none',
    border: 'none',
    color: '#94A3B8',
    cursor: 'pointer',
  },
  customerList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  customerRow: {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    padding: '12px 14px',
    border: '1px solid #E2E8F0',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  customerRowSelected: {
    borderColor: '#2563EB',
    backgroundColor: '#EFF6FF',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    accentColor: '#2563EB',
    cursor: 'pointer',
  },
  initialsAvatar: {
    width: '38px',
    height: '38px',
    borderRadius: '50%',
    backgroundColor: '#0284C7',
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '800',
    fontSize: '13px',
    flexShrink: 0,
  },
  customerInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  rowTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  custName: {
    fontSize: '14px',
    fontWeight: '800',
    color: '#0F172A',
  },
  custAmount: {
    fontSize: '14px',
    fontWeight: '800',
    color: '#DC2626',
  },
  rowBottom: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  txnDate: {
    fontSize: '11px',
    color: '#64748B',
    fontWeight: '500',
  },
  sentBadgeRow: {
    marginTop: '2px',
  },
  sentPill: {
    fontSize: '10.5px',
    color: '#059669',
    backgroundColor: '#ECFDF5',
    padding: '1px 6px',
    borderRadius: '6px',
    fontWeight: '700',
    display: 'inline-block',
  },
  setDueBtn: {
    padding: '6px 12px',
    borderRadius: '10px',
    border: '1px solid #DBEAFE',
    backgroundColor: '#EFF6FF',
    color: '#2563EB',
    fontSize: '11.5px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
  },
  bottomBar: {
    backgroundColor: '#0F172A',
    color: '#FFFFFF',
    padding: '14px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  },
  bottomBarCount: {
    fontSize: '13px',
    fontWeight: '700',
  },
  bottomBarTotal: {
    fontSize: '12px',
    color: '#34D399',
    fontWeight: '600',
  },
  bottomSetDateBtn: {
    padding: '10px 18px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: '#2563EB',
    color: '#FFFFFF',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.4)',
  },
};

export default SetCollectionDateModal;
